import {
  type IActionDefinitionDto,
  type IActionParameter,
  type IDatasourceDto,
  type IKTypeField
} from "@kuika/kuika-cl-model";
import { filterActionDefinitions } from "../../action-definition-filter";
import { listDesignerBluetoothMethods, selectDesignerBluetoothMethod } from "../../designer-bluetooth-method-actions";
import type { ScreenDesignInstance } from "../../designer-context";
import { openDatasourceForEditing } from "../../designer-view-actions";
import { getSdInstance } from "../helpers";
import { hidePlatformActionsFromLlm } from "../platform-action-visibility";
import { classifySqlMutationShape, classifySqlStatementKind } from "../sql-statement-kind";
import type { SqlMutationShape, SqlStatementKind } from "../sql-statement-kind";
import type { ToolHandlerGroup } from "../types";

function getDatasources(sd: ScreenDesignInstance): IDatasourceDto[] {
  return sd.datasourceProvider.state.datasources ?? [];
}

function getActionDefinitions(sd: ScreenDesignInstance): IActionDefinitionDto[] {
  const all: IActionDefinitionDto[] = sd.projectProvider.state.actionDefinitions ?? [];
  // KOA-18179 — LLM-facing catalog: platform-infrastructure families (audit,
  // category, document management) are withheld. They carry no description on
  // ANY surface, so the model matches them by name alone and binds app tables to
  // Kuika's own telemetry (live: `<Table datasource="GetAuditLogs">` → KV1008).
  // The human designer's picker keeps them — see platform-action-visibility.ts.
  return hidePlatformActionsFromLlm(filterActionDefinitions(all, sd));
}

/** Default result cap for `query_datasource_actions` — protects the LLM prompt. The
 *  server-side seed passes a high `limit` to fetch the COMPLETE list (never a prompt). */
const DEFAULT_ACTION_QUERY_LIMIT = 20;

interface ScoredAction {
  action: IActionDefinitionDto;
  score: number;
}

/**
 * One node in the nested return-type projection emitted alongside the flat
 * `returnFields` list. Mirrors the mcp-chat-server wire shape `ActionReturnField`
 * so the orchestrator can seed action signatures without a second fetch. A
 * scalar field carries `name` + `type`; a list/struct field additionally carries
 * `isList` and its INNER fields in `elements` — the shape the Designer needs to
 * bind a Table's row source to `ACTION.LISTFIELD` with one column per inner field.
 */
interface EmittedReturnField {
  name: string;
  type: string;
  isList?: boolean;
  elements?: EmittedReturnField[];
}

/** Root of the nested return-type projection — the action's TypeModel. */
interface EmittedReturnType {
  name?: string;
  elements: EmittedReturnField[];
}

/**
 * Recursion cap for the nested return-type projection. SAP list-of-records
 * returns are ONE level deep (list field → inner scalar fields); depth 3 leaves
 * head-room for nested DTOs while bounding the payload so a pathological deeply
 * nested C# object graph can't blow up the tool result.
 */
const MAX_RETURN_TYPE_DEPTH: number = 3;

/**
 * Recursively project a `IKTypeField[]` (a TypeModel's `elements`) into the
 * compact `EmittedReturnField[]` wire shape. A field's scalar type name is
 * `field.type?.name`; a list/struct field's inner fields live in
 * `field.elementTypeModel?.elements`. Stops descending at `MAX_RETURN_TYPE_DEPTH`.
 */
function projectReturnFields(fields: IKTypeField[] | undefined, depth: number): EmittedReturnField[] {
  if (!fields || depth >= MAX_RETURN_TYPE_DEPTH) return [];
  const out: EmittedReturnField[] = [];
  for (const el of fields) {
    if (!el.name) continue;
    const field: EmittedReturnField = { name: el.name, type: el.type?.name ?? "" };
    if (el.isList === true) field.isList = true;
    const inner: IKTypeField[] | undefined = el.elementTypeModel?.elements;
    if (inner && inner.length > 0) {
      const nested: EmittedReturnField[] = projectReturnFields(inner, depth + 1);
      if (nested.length > 0) field.elements = nested;
    }
    out.push(field);
  }
  return out;
}

/**
 * Lexical relevance of an action to the RAW query string (not the regex):
 * exact name > name prefix > name substring > displayName match. A match-all /
 * non-lexical query (e.g. `"."`) scores 0 for everything, so the createdAt
 * tiebreak (newest-first) decides — surfacing the actions the user is actively
 * working on instead of the oldest rows in creation order (the bug where a
 * blind `.slice(0,20)` returned only the project's oldest apps' actions).
 */
/**
 * The first of the candidate signature fields that actually carries parameters.
 *
 * `IActionDefinitionDto` declares both `actionParameters` and `parameters`, and
 * which one is populated depends on where the definition came from — the backend
 * fills `actionParameters` and leaves `parameters` an empty array. Preferring one
 * field outright loses the signature whenever the OTHER one is the filled one,
 * and a lost signature reads as "this action takes no arguments", which every
 * consumer gating on the signature then honours silently (KOA-18136).
 */
function firstNonEmptyParameterList(...candidates: (IActionParameter[] | undefined)[]): IActionParameter[] {
  for (const candidate of candidates) {
    if (Array.isArray(candidate) && candidate.length > 0) return candidate;
  }
  return [];
}

function actionRelevanceScore(action: IActionDefinitionDto, rawQuery: string): number {
  const q: string = rawQuery.toLowerCase().trim();
  if (q.length === 0 || q === "." || q === ".*") return 0;
  const name: string = (action.name ?? "").toLowerCase();
  const display: string = (action.displayName ?? "").toLowerCase();
  if (name === q) return 100;
  if (name.startsWith(q)) return 80;
  if (name.includes(q)) return 60;
  if (display === q) return 50;
  if (display.includes(q)) return 40;
  return 0;
}

/** Newest-first by `createdAt`. ISO timestamps compare chronologically; missing sorts
 *  oldest. Ordinal (culture-invariant) string compare — never `localeCompare`. */
function compareByCreatedAtDesc(a: IActionDefinitionDto, b: IActionDefinitionDto): number {
  const ca: string = a.createdAt ?? "";
  const cb: string = b.createdAt ?? "";
  if (ca === cb) return 0;
  return ca < cb ? 1 : -1;
}

export const datasourceTools: ToolHandlerGroup = {
  sync: {
    ui_project_list_datasources: () => {
      const sd = getSdInstance();
      const datasources = getDatasources(sd);
      if (!datasources || datasources.length === 0) return { error: "Datasources not available" };

      const list = datasources.map((ds: any) => ({
        id: ds.id,
        name: ds.name,
        type: ds.datasourceType
      }));

      return { count: list.length, datasources: list };
    },

    ui_project_get_datasource_actions: (input) => {
      const { datasourceId } = input;
      if (!datasourceId) return { error: "datasourceId is required" };

      const sd = getSdInstance();
      const datasource = getDatasources(sd).find((ds: any) => ds.id === datasourceId);
      if (!datasource) return { error: `Datasource "${datasourceId}" not found` };

      const actions = getActionDefinitions(sd)
        .filter((a: any) => a.kDatasource_Id === datasourceId)
        .map((a: any) => ({
          id: a.id,
          name: a.name,
          displayName: a.displayName || a.name,
          actionType: a.actionType
        }));

      return {
        datasourceId,
        datasourceName: datasource.name,
        count: actions.length,
        actions
      };
    },

    /**
     * Search actions by name across ALL datasources. Returns matching actions
     * with their return field names — essential for {current.field} bindings.
     *
     * Matches are RELEVANCE-RANKED (lexical match to `query`, then newest-first)
     * before the cap, so the most relevant actions survive the slice — a plain
     * `.slice(0,20)` in creation order returned only the project's OLDEST apps'
     * actions and hid the entities being worked on. `total` + `truncated` report
     * the full match count so the caller knows when to narrow the query.
     * `limit` (default 20) caps the LLM-facing payload; server-side consumers
     * (the existing-action catalog seed) pass a high `limit` to fetch the
     * COMPLETE list — its result never enters a prompt.
     */
    ui_project_query_datasource_actions: (input) => {
      const query: unknown = input.query;
      if (typeof query !== "string" || query.length === 0) return { error: "query is required" };

      const limitRaw: unknown = input.limit;
      const limit: number =
        typeof limitRaw === "number" && Number.isFinite(limitRaw) && limitRaw > 0
          ? Math.floor(limitRaw)
          : DEFAULT_ACTION_QUERY_LIMIT;

      const sd = getSdInstance();
      let regex: RegExp;
      try {
        regex = new RegExp(query, "i");
      } catch {
        regex = new RegExp(query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "i");
      }

      const scored: ScoredAction[] = getActionDefinitions(sd)
        .filter((a: IActionDefinitionDto): boolean => regex.test(a.name || "") || regex.test(a.displayName || ""))
        .map((a: IActionDefinitionDto): ScoredAction => ({ action: a, score: actionRelevanceScore(a, query) }));
      scored.sort(
        (x: ScoredAction, y: ScoredAction): number => y.score - x.score || compareByCreatedAtDesc(x.action, y.action)
      );

      const total: number = scored.length;
      const actions = scored.slice(0, limit).map(({ action: a }: ScoredAction) => {
        const fields: string[] = [];
        if (a.returnTypeModel?.elements) {
          for (const el of a.returnTypeModel.elements) {
            if (el.name) fields.push(el.name);
          }
        }
        // KOA — surface input parameters too so the orchestrator can seed
        // existing-action signatures and the Designer/Binder bind reused
        // actions' params/filters (additive — prior callers ignore this field).
        //
        // KOA-18136 — read `actionParameters` FIRST. `IActionDefinitionDto`
        // declares both `actionParameters` and `parameters`, but the backend
        // populates only the former: every action came back with
        // `parameters: []` here while `ui_project_extended.list_actions`
        // (which reads `actionParameters`) listed the very same actions WITH
        // their signatures. A declared-but-unfilled field reads as "this
        // action takes no parameters", so every consumer that gates on the
        // signature became a silent no-op — the managed-task-screen parameter
        // repair skipped every action it was built to fix and shipped a
        // pageInit read called with empty keys. `parameters` stays as the
        // fallback so a source that DOES fill it keeps working.
        // Whichever field is actually POPULATED wins — an empty `actionParameters`
        // must not shadow a filled `parameters`, or this trades one silent
        // signature loss for another.
        const declaredParameters: IActionParameter[] = firstNonEmptyParameterList(a.actionParameters, a.parameters);
        const parameters = declaredParameters.length
          ? declaredParameters.map((p: IActionParameter) => ({
              name: p.name,
              type: String(p.Typename),
              // KOA-18136 — whether a caller MAY leave this one unbound. Without
              // it every consumer had to treat "I found no source for this
              // parameter" as equally severe for a mandatory key and for an
              // optional filter, which is the difference between a dead action
              // and a wider result set. Emitted only when true, so the shape
              // stays stable for prior callers.
              ...(p.isOptionalParameter === true ? { optional: true } : {})
            }))
          : [];
        // Additively surface the FULL nested return type so the orchestrator
        // can detect a list-of-records output (an `isList` field with inner
        // scalar fields) and bind a Table's row source to `ACTION.LISTFIELD`,
        // one column per inner field. `returnFields` (top-level names) stays
        // unchanged for prior callers; `returnType` is omitted when the action
        // has no TypeModel.
        const returnType: EmittedReturnType | undefined = a.returnTypeModel
          ? { name: a.returnTypeModel.name, elements: projectReturnFields(a.returnTypeModel.elements, 0) }
          : undefined;
        // Table binding. The orchestrator matches a screen's data need against the
        // catalog by SHAPE (table + operation + params + returned columns) because
        // four naming conventions are live for the same operations on one table, so
        // a name comparison reports "missing" for an action that already exists and
        // a duplicate gets created. `tableName` has always been on the wire from
        // `ActionDefinitionModel`; it was simply never projected here. Omitted when
        // empty so actions with no single source table (REST, C#, SAP, joins) do not
        // carry a misleading "" binding.
        const tableName: string = typeof a.tableName === "string" ? a.tableName.trim() : "";
        // KOA-18227 — publish the action's READ-vs-WRITE nature, derived from its
        // SQL. Without it the orchestrator can only classify a reused action by
        // NAME, which recognises the designer's generated `<Entity>_GetAll` /
        // `<Entity>_Insert` convention and nothing else: a verb-first custom
        // mutation (`CreateExpense`) read as a query, and a non-English SELECT
        // (`Urunler_Ozet`) read as a mutation. Two short strings per action —
        // the statement itself stays out of the payload.
        //
        // Orthogonal to `tableName`: this says READ or WRITE, that says WHICH
        // TABLE. The shape matcher needs both and neither substitutes for the other.
        const sqlKind: SqlStatementKind | null = classifySqlStatementKind(a.statement);
        const mutationShape: SqlMutationShape | null = classifySqlMutationShape(a.statement);
        return {
          id: a.id,
          name: a.name,
          returnFields: fields,
          parameters,
          ...(tableName.length > 0 ? { tableName } : {}),
          ...(returnType ? { returnType } : {}),
          ...(sqlKind ? { sqlKind } : {}),
          ...(mutationShape ? { mutationShape } : {})
        };
      });

      return { count: actions.length, total, truncated: total > actions.length, actions };
    },

    ui_project_list_bluetooth_methods: (input) => {
      return listDesignerBluetoothMethods(getSdInstance(), input);
    }
  },

  async: {
    ui_common_reload_datasources: async () => {
      const sd = getSdInstance();
      // Refresh the datasource list in DatasourceProvider.state.datasources — this is what
      // the DataSources panel (this.props.sd.datasources) reads. Must be called after
      // datasource add/delete so the panel shows the updated list.
      await sd.datasourceProvider.refreshDatabases();
      // Refresh action definitions (SQL queries, swagger actions, etc.).
      // KOA-17363 — pass `skipEagerTableFetch=true` so loadActionDefinitions
      // does NOT trigger the multi-MB eager schema fetch that previously
      // caused MCP `reload_datasources` to body-timeout. Designer flows
      // keep their eager (prodhotfix) behavior by calling without the flag.
      await sd.projectProvider.loadActionDefinitions(undefined, true);
      // Slim list refresh — consumers (sql-action editor, managed-db save
      // panel, etc.) call ensureTableColumnsLoaded on demand when their UI
      // surface opens to hydrate columns.
      await sd.datasourceProvider.refreshTableIds();
      // React setState calls above are fire-and-forget — wait for state to propagate.
      await new Promise((resolve) => setTimeout(resolve, 1000));
      return {
        success: true,
        message: "Reloaded datasources, action definitions, and table schemas from backend"
      };
    },

    ui_project_navigate_to_datasource: async (input) => {
      const { datasourceId } = input;
      if (!datasourceId) return { error: "datasourceId is required" };

      const sd = getSdInstance();
      const datasource = getDatasources(sd).find((ds: any) => ds.id === datasourceId);
      if (!datasource) return { error: `Datasource "${datasourceId}" not found` };

      await openDatasourceForEditing(sd, datasourceId, {
        ensureView: true
      });

      return {
        success: true,
        message: `Opened datasource "${datasource.name}" for editing`
      };
    },

    ui_project_select_bluetooth_method: async (input) => {
      return selectDesignerBluetoothMethod(getSdInstance(), input);
    }
  }
};
